/**
 * The FormDefinition contract (docs/06). These types mirror the C# records in
 * FormForge.Modules.Forms.Definition. In Phase 2 both are generated from the JSON
 * Schema so drift becomes a build error rather than a runtime surprise; until then
 * they are maintained by hand and this file is the single place to change.
 */

export interface FormDefinition {
  schemaVersion: string;
  formRef?: string;
  type: FormTypeKey;
  title: string;
  description?: string | null;
  defaultLocale: string;
  locales: string[];
  direction: 'ltr' | 'rtl' | 'auto';
  settings: FormSettings;
  variables: FormVariable[];
  pages: FormPage[];
  logic: LogicRule[];
  /** Translated strings, keyed by locale then by the paths the server publishes. */
  translations?: Record<string, Record<string, string>> | null;
  /** Per locale, keys a machine wrote that nobody has reviewed. */
  machineTranslated?: Record<string, string[]> | null;
  scoring?: ScoringConfig | null;
  outcomes: Outcome[];
}

export type FormTypeKey =
  | 'survey' | 'quiz' | 'poll' | 'application' | 'assessment' | 'digital_form';

export interface FormSettings {
  navigation: {
    progressIndicator: 'bar' | 'steps' | 'percent' | 'none';
    allowBack: boolean;
    showQuestionNumbers: boolean;
    onePerPage: boolean;
    autoAdvance: boolean;
  };
  behaviour: {
    saveAndResume: boolean;
    resumeExpiryHours: number;
    autosaveIntervalMs: number;
    allowMultipleSubmissions: boolean;
    allowResponseEditing: boolean;
    shuffleQuestions: boolean;
    confirmBeforeSubmit: boolean;
    retainHiddenAnswers: boolean;
  };
  limits: {
    responseLimit?: number | null;
    opensUtc?: string | null;
    closesUtc?: string | null;
    onePerIdentity: 'none' | 'cookie' | 'ip' | 'identity' | 'email';
    timeLimitSeconds?: number | null;
  };
  protection: {
    captcha: 'off' | 'adaptive' | 'always';
    honeypot: boolean;
    minCompletionSeconds: number;
    requireSignedToken: boolean;
    /** When true the form only accepts submissions made through a single-use link. */
    requireInvite: boolean;
  };
  completion: {
    action: 'showOutcome' | 'redirect';
    redirectUrl?: string | null;
    redirectDelaySeconds: number;
    sendReceiptEmail: boolean;
    receiptEmailQuestionRef?: string | null;
  };
}

export interface RepeatSpec {
  min: number;
  /** Required by the server: it is what bounds the CSV export's width. */
  max: number;
  addLabel?: string | null;
  removeLabel?: string | null;
  /** `{n}` is replaced by the entry's 1-based position. */
  itemLabel?: string | null;
}

export interface FormPage {
  ref: string;
  title?: string | null;
  description?: string | null;
  elements: FormElement[];
}

export interface FormElement {
  kind: 'question' | 'group' | 'content';
  ref: string;
  type?: QuestionType;
  title?: string | null;
  description?: string | null;
  helpText?: string | null;
  placeholder?: string | null;
  required?: boolean;
  /** Opt-in per question: an embed cannot silently pre-answer what the author did not open up. */
  prefillable?: boolean;
  readOnly?: boolean;
  pii?: PiiCategory | null;
  displayAs?: string | null;
  choices?: Choice[] | null;
  /** `asis` (default), `random`, `asc` or `desc`. */
  choicesOrder?: string | null;
  rows?: MatrixRow[] | null;
  columns?: Choice[] | null;
  validation?: ValidationRule[] | null;
  layout?: { width?: 'full' | 'half' | 'third'; columns?: number } | null;
  media?: { type: string; url?: string; alt?: string; position?: string } | null;
  visibleWhen?: Condition | null;
  /**
   * Present on a group that repeats — "add another dependant".
   *
   * Typed here rather than left to survive as an unknown field, because the store round
   * trips the whole definition object: an untyped field works by accident until somebody
   * rebuilds an element and drops it, and the loss would be silent.
   */
  repeat?: RepeatSpec | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  labels?: { low?: string; high?: string } | null;
  expression?: string | null;
  source?: string | null;
  sourceKey?: string | null;
  elements: FormElement[];
  contentType?: string | null;
  html?: string | null;
}

export type PiiCategory =
  | 'name' | 'email' | 'phone' | 'address'
  | 'national_id' | 'health' | 'financial' | 'biometric';

export interface Choice {
  value: string;
  label: string;
  imageUrl?: string | null;
  /** "Other, please specify" — the typed text is submitted as this answer's value. */
  allowText?: boolean;
  /** Selecting this clears the rest. Multiple-choice only; refused at publish elsewhere. */
  exclusive?: boolean;
  score?: number | null;
}

export interface MatrixRow { ref: string; label: string }

export interface ValidationRule {
  rule: string;
  value?: unknown;
  message?: string | null;
  region?: string | null;
}

export interface FormVariable {
  ref: string;
  type: 'string' | 'number' | 'boolean';
  /** Only "token" values are trusted; "query" values are attacker-supplied (docs/08 §6). */
  source: 'query' | 'token' | 'computed' | 'literal';
  sourceKey?: string | null;
  expression?: string | null;
  default?: unknown;
  required?: boolean;
}

export interface LogicRule {
  ref: string;
  trigger: 'onLoad' | 'onChange' | 'onPageEnter' | 'onPageSubmit' | 'onSubmit';
  when?: Condition | null;
  scope?: { page?: string } | null;
  actions: LogicAction[];
  reversible?: boolean;
}

export interface LogicAction {
  type: LogicActionType;
  target?: ActionTarget | null;
  value?: unknown;
}

export type LogicActionType =
  | 'show' | 'hide' | 'enable' | 'disable' | 'setRequired' | 'setValue'
  | 'clearValue' | 'jumpTo' | 'skipPage' | 'screenOut' | 'setChoices'
  | 'addTag' | 'setOutcome';

export interface ActionTarget {
  question?: string;
  group?: string;
  page?: string;
  outcome?: string;
  variable?: string;
}

/** Conditions are data, not code: diffable, analysable, and renderable in a builder. */
export interface Condition {
  op: string;
  conditions?: Condition[] | null;
  left?: Operand | null;
  right?: unknown;
}

export interface Operand {
  ref?: string;
  var?: string;
  row?: string;
  system?: string;
  expr?: string;
}

export interface ScoringConfig {
  enabled: boolean;
  mode: string;
  maxScore: number;
  passThreshold?: number | null;
  revealMode: 'never' | 'afterSubmit' | 'perQuestion' | 'afterReview';
  excludeUnansweredFromMax?: boolean;
  sections: ScoringSection[];
  questions: Record<string, QuestionScoring>;
  bands: ScoringBand[];
}

export interface ScoringSection {
  ref: string; label: string; weight: number; questionRefs: string[];
}

export interface QuestionScoring {
  points: number;
  type: 'exact' | 'partial_credit' | 'per_choice' | 'numeric_range' | 'expression' | 'manual';
  correct?: string[] | null;
  partialCredit?: boolean;
  penaltyPerWrong?: number;
  minPoints?: number;
  correctMin?: number | null;
  correctMax?: number | null;
  choiceScores?: Record<string, number> | null;
}

export interface ScoringBand {
  ref: string; min: number; label: string; outcomeRef?: string | null;
}

export interface Outcome {
  ref: string;
  /** null marks the fallback, which must be last. Publish fails without one. */
  when?: Condition | null;
  title: string;
  body?: string | null;
  showScore?: boolean;
  showAnswerReview?: boolean;
  actions: { type: string; label: string; url?: string; target?: string }[];
}

// ---- Question catalogue -----------------------------------------------------------

export type QuestionType =
  | 'short_text' | 'long_text' | 'email' | 'phone' | 'url' | 'number' | 'currency'
  | 'password'
  | 'single_select' | 'multi_select' | 'dropdown' | 'searchable_dropdown'
  | 'image_choice' | 'yes_no' | 'likert'
  | 'rating' | 'nps' | 'slider' | 'star' | 'emoji_scale'
  | 'ranking' | 'matrix_single' | 'matrix_multi'
  | 'date' | 'time' | 'datetime'
  | 'address' | 'country' | 'file_upload' | 'signature'
  | 'section_header' | 'hidden' | 'calculated'

  // Studio-only. Choosing it builds a group element, never a question of this type, so
  // nothing with this name is ever sent to the server - see addLayoutGroup.
  | 'columns';

export type QuestionCategory = 'Text' | 'Choice' | 'Scale' | 'Date' | 'Advanced' | 'Layout';

export interface QuestionTypeMeta {
  type: QuestionType;
  label: string;
  category: QuestionCategory;
  /** Key into the icon set in components/icons.tsx. */
  icon: IconKey;
  hasChoices?: boolean;
  hasRows?: boolean;

  /**
   * A matrix scores its rows against a shared set of columns, and the runtime renders
   * `columns` — never `choices`. Matrices were declared `hasChoices`, so the builder seeded
   * choices the runtime ignores and no columns at all, which is exactly what the publish
   * validator refuses. Kept as its own flag so the two editors stay distinguishable.
   */
  hasColumns?: boolean;

  /** Adds a layout container rather than a question. Nothing answers it. */
  isContainer?: boolean;
  scoreable?: boolean;
  piiCapable?: boolean;
  /**
   * The runtime cannot render this type yet. The palette shows it greyed out rather than
   * hiding it, so the roadmap is visible instead of the type looking unsupported forever.
   */
  unavailable?: boolean;
  /**
   * Whether this type may sit inside a repeating group.
   *
   * Mirrors QuestionTypes.CanRepeat on the server, which is authoritative and refuses the
   * rest at publish. Kept here only so the palette does not offer something that will be
   * rejected — the check that matters is the server's.
   */
  repeatable?: boolean;
}

/**
 * Types whose interaction is wired by their bare ref, so two entries would share one
 * control. Mirrors NotRepeatable in QuestionTypes.cs.
 */
export const NOT_REPEATABLE: ReadonlySet<string> = new Set([
  'rating', 'nps', 'slider', 'star', 'emoji_scale',
  'ranking', 'matrix_single', 'matrix_multi',
  'file_upload', 'signature', 'calculated',
  'section_header', 'page_break', 'hidden',
]);

type IconKey = string;

/**
 * Drives the palette, the properties panel, and export/analytics behaviour from data
 * rather than a switch statement repeated in five places (docs/06 §4.2).
 */
export const QUESTION_TYPES: QuestionTypeMeta[] = [
  { type: 'short_text', label: 'Short text', category: 'Text', icon: 'text', piiCapable: true },
  { type: 'long_text', label: 'Long text', category: 'Text', icon: 'paragraph' },
  { type: 'email', label: 'Email', category: 'Text', icon: 'at', piiCapable: true },
  { type: 'phone', label: 'Phone', category: 'Text', icon: 'phone', piiCapable: true },
  { type: 'url', label: 'URL', category: 'Text', icon: 'link' },
  { type: 'number', label: 'Number', category: 'Text', icon: 'hash', scoreable: true },
  { type: 'currency', label: 'Currency', category: 'Text', icon: 'currency', scoreable: true },
  { type: 'password', label: 'Masked text', category: 'Text', icon: 'eyeOff', piiCapable: true },

  { type: 'single_select', label: 'Single choice', category: 'Choice', icon: 'radio', hasChoices: true, scoreable: true },
  { type: 'multi_select', label: 'Multiple choice', category: 'Choice', icon: 'checkbox', hasChoices: true, scoreable: true },
  { type: 'dropdown', label: 'Dropdown', category: 'Choice', icon: 'chevronDown', hasChoices: true, scoreable: true },
  { type: 'searchable_dropdown', label: 'Searchable dropdown', category: 'Choice', icon: 'search', hasChoices: true, scoreable: true },
  { type: 'image_choice', label: 'Image choice', category: 'Choice', icon: 'image', hasChoices: true, scoreable: true },
  { type: 'yes_no', label: 'Yes / No', category: 'Choice', icon: 'toggle', scoreable: true },
  { type: 'likert', label: 'Likert scale', category: 'Choice', icon: 'scale', hasChoices: true },
  { type: 'ranking', label: 'Ranking', category: 'Choice', icon: 'rank', hasChoices: true },

  { type: 'rating', label: 'Rating', category: 'Scale', icon: 'star', scoreable: true },
  { type: 'nps', label: 'NPS', category: 'Scale', icon: 'gauge', scoreable: true },
  { type: 'slider', label: 'Slider', category: 'Scale', icon: 'slider', scoreable: true },
  { type: 'star', label: 'Star rating', category: 'Scale', icon: 'star', scoreable: true },
  { type: 'emoji_scale', label: 'Smiley scale', category: 'Scale', icon: 'scale', scoreable: true },

  { type: 'date', label: 'Date', category: 'Date', icon: 'calendar' },
  { type: 'time', label: 'Time', category: 'Date', icon: 'clock' },
  { type: 'datetime', label: 'Date & time', category: 'Date', icon: 'calendarClock' },

  { type: 'matrix_single', label: 'Matrix (single)', category: 'Advanced', icon: 'grid', hasColumns: true, hasRows: true },
  { type: 'matrix_multi', label: 'Matrix (multi)', category: 'Advanced', icon: 'gridCheck', hasColumns: true, hasRows: true },
  { type: 'country', label: 'Country', category: 'Advanced', icon: 'globe', hasChoices: true },
  { type: 'file_upload', label: 'File upload', category: 'Advanced', icon: 'upload' },
  { type: 'signature', label: 'Signature', category: 'Advanced', icon: 'signature', piiCapable: true },
  { type: 'address', label: 'Address', category: 'Advanced', icon: 'page', piiCapable: true, unavailable: true },

  { type: 'section_header', label: 'Section header', category: 'Layout', icon: 'heading' },

  // Not a question: dropping this adds a container and questions go inside it. The palette
  // is where somebody looks for "how do I put these two side by side", so it lives here
  // rather than behind a button somewhere else.
  { type: 'columns', label: 'Columns', category: 'Layout', icon: 'grid', isContainer: true },
  { type: 'hidden', label: 'Hidden field', category: 'Layout', icon: 'eyeOff', unavailable: true },
  { type: 'calculated', label: 'Calculated', category: 'Layout', icon: 'function' },
];

/** Tints the palette and the canvas so a question's kind is legible at a glance. */
export const CATEGORY_TINT: Record<QuestionCategory, string> = {
  Text: 'text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/40',
  Choice: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/40',
  Scale: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40',
  Date: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40',
  Advanced: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40',
  Layout: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/60',
};

export const typeMeta = (type?: string): QuestionTypeMeta | undefined =>
  QUESTION_TYPES.find((t) => t.type === type);

// ---- API response shapes ----------------------------------------------------------

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresUtc: string;
  user: { id: string; email: string; displayName?: string | null };
  tenant: { id: string; name: string; slug: string; defaultWorkspaceId: string };
}

/**
 * What the sign-in page needs before it can authenticate anything, which is why the
 * endpoint behind it is anonymous. Nothing here is per-account: under "adaptive" the page
 * finds out a challenge is owed by being told so when it tries.
 */
export interface AuthCaptchaConfig {
  provider: string;
  siteKey: string;
  mode: 'off' | 'adaptive' | 'always';
  registerRequired: boolean;
}

/**
 * One page of the forms list. `page` is what the server actually served, which is not
 * always what was asked for: a stale page number past the end is clamped rather than
 * answered with a blank screen, and the caller corrects its URL from this.
 */
export interface FormListPage {
  data: FormSummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface FormSummary {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  status: string;
  questionCount: number;
  responseCount: number;
  createdUtc: string;
  modifiedUtc?: string | null;
  publishedVersionId?: string | null;
  /** The folder this form is filed in, or null. */
  projectId?: string | null;
  lastResponseUtc?: string | null;
  /** Response counts for each of the last seven days, oldest first. */
  recent?: number[];
}

export interface FormDetail {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  status: string;
  defaultLocale: string;
  opensUtc?: string | null;
  closesUtc?: string | null;
  responseLimit?: number | null;
  responseCount: number;
  /** The theme applied to this form, or null to fall back to the workspace default. */
  themeId?: string | null;
  createdUtc: string;
  modifiedUtc?: string | null;
  draftVersion?: { id: string; number: number; questionCount: number } | null;
  publishedVersion?: {
    id: string; number: number; questionCount: number;
    versionKey: string; publishedUtc: string;
  } | null;
}

export interface ProjectDto {
  id: string;
  name: string;
  color?: string | null;
  position: number;
  workspaceId: string;
  parentId?: string | null;
  formCount: number;
  createdUtc: string;
}

export interface ValidationIssue { path: string; code: string; message: string }

export interface SaveDefinitionResult {
  versionId: string;
  questionCount: number;
  pageCount: number;
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export interface PublishResult {
  versionId: string;
  versionNumber: number;
  versionKey: string;
  questionCount: number;
  publishedUtc: string;
  nextDraftVersionId: string;
  publicUrl: string;
  unchanged?: boolean;
  warnings: ValidationIssue[];
}

export interface ResponseRow {
  id: string;
  sequenceNumber: number;
  status: string;
  submittedUtc: string;
  durationMs?: number | null;
  channel: string;
  identity?: string | null;
  answers: Record<string, unknown>;
  score?: { totalScore: number; maxScore: number; percentage: number; passed?: boolean } | null;
}

export interface FormAnalytics {
  formId: string;
  views: number;
  starts: number;
  responses: number;
  completions: number;
  completionRate: number;
  medianDurationMs?: number | null;
  p90DurationMs?: number | null;
  choiceDistribution: Record<string, Record<string, number>>;
  numericStats: Record<string, { count: number; mean: number; min: number; max: number }>;

  /** One entry per day that has been rolled up. Absent days simply had no activity. */
  series?: {
    date: string;
    starts: number;
    responses: number;
    completions: number;
    meanDurationMs?: number | null;
  }[];

  /**
   * How much of the total came from rows the rollup worker has not folded in yet. The
   * figures are complete either way; this says how they were assembled.
   */
  freshness?: { rolledUpTo: number; liveResponses: number; note: string };
}

export interface EmbedConfigDto {
  id: string;
  name: string;
  mode: string;
  renderMode: string;
  requireSignedToken: boolean;
  isActive: boolean;
  origins: string[];
  snippet: string;
  warning?: string | null;
}

// ---- companies, notifications, audit, invites ---------------------------------------

// `id` is the public id string the API returns, as everywhere else. It is not the
// database row id, and there is no separate `publicId` field on the wire.
export interface CompanyDto {
  id: string;
  name: string;
  slug: string;
  legalName?: string;
  contactEmail?: string;
  status: string;
  isDefault: boolean;
  createdUtc: string;
  workspaceCount: number;
  formCount: number;
}

export interface NotificationDto {
  id: string;
  kind: string;
  title: string;
  body?: string;
  url?: string;
  entityType?: string;
  createdUtc: string;
  readUtc?: string | null;
}

export interface AuditEntryDto {
  id: string;
  action: string;
  actorType: string;
  actorEmail?: string;
  entityType?: string;
  entityId?: number;
  entityName?: string;
  result: string;
  reason?: string;
  occurredUtc: string;
  beforeJson?: string;
  afterJson?: string;
}

/** One set of audit entries. The cursor is opaque — anchor on it, never parse it. */
export interface AuditPage {
  data: AuditEntryDto[];
  hasMore: boolean;
  nextCursor?: string | null;
}

export interface InviteDto {
  id: string;
  /** First few characters only. Never enough to open the form. */
  tokenPrefix: string;
  recipientEmail?: string;
  recipientRef?: string;
  status: 'active' | 'consumed' | 'revoked' | 'expired';
  useCount: number;
  maxUses: number;
  expiresUtc?: string;
  firstOpenedUtc?: string;
  consumedUtc?: string;
  createdUtc: string;
}

export interface ShareLinkDto {
  id: string;
  slug: string;
  shortCode: string;
  url: string;
  kind: 'public' | 'private' | 'preview' | 'personal';
  /** Whether an access code is set. The hash itself never leaves the server. */
  hasPassword: boolean;
  isActive: boolean;
  opensUtc?: string | null;
  closesUtc?: string | null;
  maxResponses?: number | null;
  responseCount: number;
  remaining?: number | null;
  createdUtc: string;
}

// ---- team, workspaces and account ---------------------------------------------------

export interface TeamMemberDto {
  id: number;
  userId: string;
  email: string;
  displayName?: string;
  emailVerified: boolean;
  role: string;
  roleName: string;
  status: string;
  joinedUtc?: string;
  lastActiveUtc?: string;
  isSelf: boolean;
  mfaEnabled: boolean;
}

export interface RoleDto {
  key: string;
  name: string;
  description?: string;
  permissions: string[];
}

export interface InvitationDto {
  id: string;
  email: string;
  role: string;
  status: 'Pending' | 'Accepted' | 'Revoked' | 'Expired';
  expiresUtc: string;
  invitedByName?: string;
  lastSentUtc?: string;
  acceptedUtc?: string;
  createdUtc: string;
}

export interface WorkspaceDto {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
  companyId?: string;
  formCount: number;
  createdUtc: string;
}

export interface TenantMembershipDto {
  id: string;
  name: string;
  slug: string;
  role: string;
  requireMfa: boolean;
  isCurrent: boolean;
  joinedUtc?: string;
}

export interface MfaStatusDto {
  enabled: boolean;
  enrolling: boolean;
  method?: string;
  confirmedUtc?: string;
  recoveryCodesRemaining: number;
  requiredByWorkspace: boolean;
}

/**
 * The values a theme sets. Every colour is a hex string — the API refuses anything else,
 * because these end up inside CSS custom properties on a page served to respondents.
 */
export interface ThemeTokensDto {
  primary: string;
  primaryHover: string;
  onPrimary: string;
  page: string;
  surface: string;
  surfaceMuted: string;
  surfaceRaised: string;
  text: string;
  textMuted: string;
  border: string;
  borderSubtle: string;
  focus: string;
  danger: string;
  dangerSoft: string;
  fontFamily: string;
  fontSizePx: number;
  radiusPx: number;
  maxWidthPx: number;
}

/** One measured WCAG pair. `blocking` pairs refuse the save; the rest are advice. */
export interface ContrastPairDto {
  label: string;
  foreground: string;
  background: string;
  ratio: number;
  required: number;
  blocking: boolean;
  passes: boolean;
}

export interface ThemeDto {
  id: string;
  name: string;
  isDefault: boolean;
  revision: string;
  tokens: ThemeTokensDto;
  contrast?: ContrastPairDto[];
  stylesheetUrl: string;
  /** Only present in the list response; a single theme is not asked to count its forms. */
  formCount?: number;
  createdUtc: string;
  modifiedUtc?: string;
}

export interface TranslatableKeyDto {
  key: string;
  source: string;
  description: string;
  /** Interface strings are shared by every form and counted separately from content. */
  isInterface: boolean;
}

export interface TranslationsDto {
  formId: string;
  defaultLocale: string;
  locales: string[];
  direction: string;
  keys: TranslatableKeyDto[];
  /** Per locale, excluding the default: how many content strings are done. */
  progress: Record<string, { done: number; total: number }>;
  translations: Record<string, Record<string, string>>;
  /** Per locale, the keys a machine wrote that nobody has checked. */
  machineTranslated: Record<string, string[]>;
  /** False when no provider is set up, in which case the studio hides the button. */
  canAutoTranslate: boolean;
  provider: string;
}

export interface AutoTranslateDto {
  locale: string;
  provider: string;
  translated: Record<string, string>;
  /** Strings whose placeholders did not survive; these need writing by hand. */
  skipped: string[];
  message?: string;
}

export interface ThemePreviewDto {
  valid: boolean;
  problems: { path: string; code: string; message: string }[];
  contrast: ContrastPairDto[];
  css: string | null;
}

/** One signed-in device. A session is a refresh-token family, not a single token. */
export interface SessionDto {
  id: string;
  signedInUtc: string;
  lastSeenUtc: string;
  expiresUtc: string;
  userAgent?: string | null;
  device: string;
  workspace?: string | null;
  isCurrent: boolean;
}

export interface ApiKeyDto {
  id: string;
  name: string;
  keyPrefix: string;
  environment: string;
  scopes: string[];
  allowedIps: string[];
  expiresUtc?: string | null;
  lastUsedUtc?: string | null;
  revokedUtc?: string | null;
  createdUtc: string;
  status: 'active' | 'revoked' | 'expired';
}

/** The create response, and the only time the secret exists outside the caller. */
export interface ApiKeyCreatedDto extends Omit<ApiKeyDto, 'status' | 'lastUsedUtc' | 'revokedUtc'> {
  secret: string;
  warning: string;
}

export interface RollupStatusDto {
  formId: string;
  rolledUpDays: number;
  responseWatermark: number;
  answerWatermark: number;
  pendingResponses: number;
  pendingAnswers: number;
}

/* ---- the response inbox (docs/11, docs/12) ---------------------------------------
 *
 * A second way into responses that does not start from a form. The per-form list keeps
 * its own `ResponseRow`: it is a different shape for a different job — every answer of
 * one form's responses — and merging the two would make both worse.
 */

/** One row of the cross-form feed. Carries a short preview rather than every answer. */
export interface InboxRow {
  id: string;
  form: { id: string; name: string; type: string };
  sequenceNumber: number;
  status: string;
  submittedUtc: string;
  durationMs?: number | null;
  channel: string;
  identity?: string | null;
  score?: { totalScore: number; maxScore: number; percentage: number; passed?: boolean } | null;
  answerCount: number;
  /** Whether the response contains PII-classified answers, whether or not they are shown. */
  hasPii: boolean;
  preview: { questionRef: string; label: string; value: string; pii: boolean }[];
}

export interface InboxPage {
  data: InboxRow[];
  hasMore: boolean;
  nextCursor?: string | null;
  /** True when personal data in this page came back masked. */
  piiMasked: boolean;
  /** Whether this caller holds response:read_pii at all. */
  canRevealPii: boolean;
}

export interface InboxQuery {
  q?: string;
  formId?: string;
  /** Only responses that answered this question. */
  questionRef?: string;
  companyId?: string;
  projectId?: string;
  status?: string;
  channel?: string;
  /** `yyyy-mm-dd` in the viewer's own timezone; both bounds are inclusive days. */
  from?: string;
  /** @see from */
  to?: string;
  scored?: boolean;
  reveal?: boolean;
  limit?: number;
  cursor?: string;
}

export interface ResponseFileRef {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: string;
  downloadUrl: string;
}

/** One question and what was answered, already resolved against the version's labels. */
export interface AnswerNode {
  kind: 'question';
  ref: string;
  title: string;
  type?: string | null;
  description?: string | null;
  /** The PII category, when the question collects personal data. */
  pii?: string | null;
  masked: boolean;
  answered: boolean;
  value?: unknown;
  /** The value as one line of text, with choice values already resolved to labels. */
  display: string;
  choices?: { value: string; label: string; selected: boolean }[] | null;
  matrix?: {
    rows: { ref: string; label: string; value: string }[];
    columns?: { value: string; label: string }[] | null;
  } | null;
  files?: ResponseFileRef[] | null;
}

export interface AnswerGroupNode {
  kind: 'group';
  ref: string;
  title?: string | null;
  description?: string | null;
  repeating: boolean;
  /** Set on a plain group. */
  elements?: AnswerNode[];
  /** Set on a repeating group: one block per entry, so rows stay associated. */
  entries?: { label: string; elements: AnswerNode[] }[];
}

export type AnswerElement = AnswerNode | AnswerGroupNode;

export interface AnswerPage {
  ref: string;
  title?: string | null;
  description?: string | null;
  elements: AnswerElement[];
}

export interface ResponseDetail {
  id: string;
  sequenceNumber: number;
  status: string;
  submittedUtc: string;
  durationMs?: number | null;
  channel: string;
  identity?: string | null;
  form: { id: string; name: string; type: string; versionNumber?: number | null };
  pages: AnswerPage[];
  /** Answers with no question left to sit under — never silently dropped. */
  extras: { ref: string; value: string; pii?: string | null; masked: boolean }[];
  calculated?: { ref: string; value: string }[] | null;
  score?: {
    totalScore: number;
    maxScore: number;
    percentage: number;
    passed?: boolean | null;
    outcomeBandKey?: string | null;
    sections?: unknown;
    perQuestion?: unknown;
  } | null;
  answered: number;
  questionCount: number;
  hasPii: boolean;
  piiMasked: boolean;
  canRevealPii: boolean;
  /** Neighbours within the same form, so a deep link still has somewhere to go. */
  newerId?: string | null;
  olderId?: string | null;
}

/* ---- the generated form dashboard (docs/25) ---------------------------------------
 *
 * The server picks the widget; the client renders the kind. Keeping selection on the
 * server is what lets the same descriptor render to a PDF and a scheduled email without a
 * second selection rule drifting out of step with this one.
 */

export type WidgetKind =
  | 'bar' | 'orderedBar' | 'histogram' | 'nps' | 'timeline' | 'matrix'
  | 'numeric' | 'textSamples' | 'files' | 'countOnly' | 'notSummarised';

export interface DashboardBar {
  key: string;
  label: string;
  /** Null when suppressed as a small cell on a PII-classified question. */
  count: number | null;
  percent: number | null;
}

export interface DashboardWidget {
  kind: WidgetKind;
  questionRef: string;
  title: string;
  type?: string | null;
  pii: boolean;
  /** Respondents who answered — not the projected row count, which multi-select inflates. */
  answered?: number | null;
  answerRate?: number | null;
  /** What the percentages are of. Printed on the chart rather than assumed. */
  denominator?: string | null;
  data?: DashboardBar[] | null;
  numeric?: {
    count: number; mean: number; stdDev: number; min: number; max: number;
  } | null;
  /** One distribution per matrix row, so the rows can be compared. */
  matrix?: {
    columns: { key: string; label: string }[];
    rows: {
      ref: string; label: string; total: number;
      cells: { key: string; label: string; count: number; percent: number }[];
    }[];
  } | null;
  /** Promoters minus detractors as a share — the metric, not the mean. */
  nps?: {
    promoters: number; passives: number; detractors: number; total: number; score: number;
  } | null;
  /** Stated limits on what this widget can claim. Never hidden from the reader. */
  caveats: string[];
}

export interface DashboardSection {
  title: string;
  description?: string | null;
  widgets: DashboardWidget[];
}

export interface FormDashboard {
  form: {
    id: string; name: string; type: string; status: string;
    versionNumber?: number | null;
    /** How many versions the responses in range were answered against. */
    versionsInRange: number;
  };
  range: { from?: string | null; to?: string | null; days?: number | null };
  totals: {
    /** Null when a date range is set: views are a running counter with no per-day row. */
    views: number | null;
    starts: number;
    responses: number;
    completions: number;
    completionRate: number;
    medianDurationMs?: number | null;
    p90DurationMs?: number | null;
  };
  series: { date: string; starts: number; responses: number; completions: number }[];
  /** How current the figures are, stated rather than left to be assumed. */
  freshness: { rolledUpTo: number; liveResponses: number };
  /** Where people stopped. Null when no session has been rolled up yet. */
  funnel?: {
    steps: { ref: string; title: string; reached: number; droppedHere: number }[];
    completed: number;
    note: string;
  } | null;
  /** Sessions split by the device they were on. */
  devices: {
    key: string; label: string; starts: number; completions: number; completionRate: number;
  }[];
  sections: DashboardSection[];
}

/* ---- the account's own identity -------------------------------------------------- */

/** What a respondent sees above a form. Every field optional; every one has a fallback. */
export interface OrganisationBranding {
  displayName?: string | null;
  /** Absolute https URL, the same rule themes apply to their own logo. */
  logoUrl?: string | null;
  supportEmail?: string | null;
  websiteUrl?: string | null;
  footerText?: string | null;
}

export interface OrganisationDto {
  id: string;
  name: string;
  slug: string;
  branding: OrganisationBranding;
  /** Branding with the fallbacks already applied — what a form will actually show. */
  effective: OrganisationBranding;
}
